import React, { useState, useEffect } from "react";
import PageSeo from "../components/seo/PageSeo";
import { Helmet } from "react-helmet";
import "./ApiDocsPage.css";
import { copyText } from "../utils/clipboard";
import { useTranslation, Trans } from "react-i18next";

/* API DOCS — design "Signal" 4g
   Textes (titres, descriptions de parametres, erreurs) : cles api.* des
   translation.json ; les champs anglais ci-dessous ne servent que de repli.
   Les exemples curl / JSON et les codes (NO_NUMBERS, 406...) restent tels quels.
   Deux onglets : REST (notre API) et SMS-Activate (façade handler_api.php). */

const BASE = "https://api.2fasms.com";
const SA = `${BASE}/stubs/handler_api.php`;

// <code> des textes traduits (api.intro, api.sa.*).
const CODE = (
  <code className="sg-mono" style={{ background: "var(--color-canvas)", border: "1px solid var(--color-line)", borderRadius: 4, padding: "1px 5px", fontSize: 13 }} />
);

const ENDPOINTS = [
  {
    id: "balance",
    tab: "rest",
    method: "GET",
    path: "/order/api/get_balance",
    summary: "Get your balance",
    auth: true,
    params: [],
    request: `curl -H "Authorization: Bearer YOUR_API_KEY" \\\n     "${BASE}/order/api/get_balance"`,
    response: `{\n  "balance": 25.50,\n  "user_id": 123\n}`,
    errors: "400 Missing API key · 401 Invalid API key",
  },
  {
    id: "countries",
    tab: "rest",
    method: "GET",
    path: "/order/api/countries",
    summary: "List available countries",
    auth: false,
    params: [],
    request: `curl "${BASE}/order/api/countries"`,
    response: `{\n  "countries": [\n    { "code": "france", "name": "France", "services_count": 156 }\n  ],\n  "total": 194,\n  "from_cache": true\n}`,
    errors: "",
  },
  {
    id: "services",
    tab: "rest",
    method: "GET",
    path: "/order/api/services",
    summary: "List services (all, or per country)",
    auth: false,
    params: [
      { name: "country", type: "string", desc: "Optional country code, e.g. france" },
    ],
    request: `curl "${BASE}/order/api/services?country=france"`,
    response: `{\n  "country": "france",\n  "services": [\n    {\n      "name": "whatsapp",\n      "providers": ["provider_5", "provider_7", "provider_9"],\n      "min_price": 15.20,\n      "max_price": 33.50\n    }\n  ],\n  "total": 89,\n  "from_cache": true\n}`,
    errors: "404 Country not found · 503 No data available",
  },
  {
    id: "buy",
    tab: "rest",
    method: "POST",
    path: "/order/api/get_number",
    summary: "Buy an activation number",
    auth: true,
    params: [
      { name: "country", type: "string", desc: "Country slug, e.g. france" },
      { name: "service", type: "string", desc: "Service slug, e.g. whatsapp" },
    ],
    request: `curl -X POST \\\n     -H "Authorization: Bearer YOUR_API_KEY" \\\n     -H "Content-Type: application/json" \\\n     -d '{"country":"france","service":"whatsapp"}' \\\n     "${BASE}/order/api/get_number"`,
    response: `{\n  "orderId": 12345,\n  "number": "+33123456789",\n  "status": "ok"\n}`,
    errors: "400 Invalid parameters · 406 Insufficient funds · 500 Service/country unavailable",
  },
  {
    id: "check",
    tab: "rest",
    method: "POST",
    path: "/order/api/check_order",
    summary: "Poll the SMS code",
    auth: true,
    params: [{ name: "orderId", type: "int", desc: "Order id returned by get_number" }],
    request: `curl -X POST \\\n     -H "Authorization: Bearer YOUR_API_KEY" \\\n     -H "Content-Type: application/json" \\\n     -d '{"orderId":12345}' \\\n     "${BASE}/order/api/check_order"`,
    response: `{\n  "status": "success",\n  "data": [{ "orderId": 12345, "sms": "123456" }]\n}`,
    errors: "400 Missing parameters · 404 Order not found",
  },
  {
    id: "finish",
    tab: "rest",
    method: "POST",
    path: "/order/api/finish_order",
    summary: "Finish an order",
    auth: true,
    params: [{ name: "orderId", type: "int", desc: "Order id to finish" }],
    request: `curl -X POST \\\n     -H "Authorization: Bearer YOUR_API_KEY" \\\n     -H "Content-Type: application/json" \\\n     -d '{"orderId":12345}' \\\n     "${BASE}/order/api/finish_order"`,
    response: `{\n  "message": "order has been finished"\n}`,
    errors: "404 Order not found · 406 Already cancelled / expired / SMS pending",
  },
  {
    id: "webhook",
    tab: "rest",
    method: "POST",
    path: "(your URL) — set via POST /user/webhook",
    summary: "Webhook — get the SMS pushed to you",
    auth: true,
    params: [
      { name: "url", type: "string", desc: "Your HTTPS endpoint. POST {\"url\":\"...\"} to /user/webhook to set it, empty to disable." },
    ],
    request: `# 1) register your webhook\ncurl -X POST -H "Authorization: Bearer YOUR_API_KEY" \\\n     -H "Content-Type: application/json" \\\n     -d '{"url":"https://your-app.com/sms-hook"}' \\\n     "${BASE}/user/webhook"\n\n# 2) we POST each received SMS to it`,
    response: `{\n  "activationId": "12345",\n  "service": "whatsapp",\n  "country": "france",\n  "text": "Your code is 123456",\n  "code": "123456",\n  "number": "+33123456789",\n  "receivedAt": "2026-08-26 17:00:00"\n}`,
    errors: "Retried up to 3 times on non-2xx. No webhook set = poll getStatus / check_order instead.",
  },
];

// Parametres communs a getNumber / getNumberV2 (memes objets, une seule source).
const NUMBER_PARAMS = [
  { name: "api_key", type: "string", desc: "Your API key" },
  { name: "service", type: "string", desc: "Service code (wa, tg, go, fb, ig…) or slug (whatsapp)" },
  { name: "country", type: "string", desc: "Country code (187 = USA, 16 = UK, 78 = France, 0 = Russia…) or slug (usa)" },
  { name: "max_price", type: "float", desc: "Optional. Refuse if the price is higher (USD)" },
];

const SA_KEY = { name: "api_key", type: "string", desc: "Your API key (same key as the REST API)" };
const SA_ID = { name: "id", type: "string", desc: "activationId returned by getNumber" };

/* Façade sms-activate : meme handler_api.php, reponses en texte brut (sauf les
   actions V2 / listes qui renvoient du JSON). L'URL de base est affichee en haut,
   le path ne porte donc que la query. */
const SA_ENDPOINTS = [
  {
    id: "getBalance",
    tab: "sa",
    method: "GET",
    path: "?action=getBalance",
    summary: "Get your balance",
    auth: true,
    params: [SA_KEY],
    request: `curl "${SA}?api_key=YOUR_API_KEY&action=getBalance"`,
    response: `ACCESS_BALANCE:25.50\n\n# BAD_KEY  wrong or missing api_key`,
    errors: "",
  },
  {
    id: "getNumber",
    tab: "sa",
    method: "GET",
    path: "?action=getNumber",
    summary: "Buy a number",
    auth: true,
    params: NUMBER_PARAMS,
    request: `curl "${SA}?api_key=YOUR_API_KEY&action=getNumber&service=wa&country=187"`,
    response: `ACCESS_NUMBER:12345:+15551234567\n\n# NO_NUMBERS          no number available right now\n# NO_MONEY            balance too low\n# MAX_PRICE_EXCEEDED  price above max_price\n# BAD_SERVICE         unknown service / country pair\n# BAD_KEY             wrong or missing api_key`,
    errors: "The price paid is also returned in the X-Price response header.",
  },
  {
    id: "getNumberV2",
    tab: "sa",
    method: "GET",
    path: "?action=getNumberV2",
    summary: "Buy a number (JSON)",
    auth: true,
    params: NUMBER_PARAMS,
    request: `curl "${SA}?api_key=YOUR_API_KEY&action=getNumberV2&service=wa&country=187"`,
    response: `{\n  "activationId": "12345",\n  "phoneNumber": "+15551234567",\n  "activationCost": "0.25",\n  "currency": 840,\n  "countryCode": "187",\n  "canGetAnotherSms": "1",\n  "activationTime": "2026-11-01 12:00:00",\n  "activationOperator": "any"\n}`,
    errors: "On failure, the same plain-text codes as getNumber.",
  },
  {
    id: "getStatus",
    tab: "sa",
    method: "GET",
    path: "?action=getStatus",
    summary: "Poll the SMS code",
    auth: true,
    params: [SA_KEY, SA_ID],
    request: `curl "${SA}?api_key=YOUR_API_KEY&action=getStatus&id=12345"`,
    response: `STATUS_OK:123-456     # code received\n# STATUS_WAIT_CODE    # still waiting\n# STATUS_CANCEL       # cancelled or expired\n# NO_ACTIVATION       # unknown id`,
    errors: "Poll every 3 seconds or more, or set a webhook (REST tab) to get the SMS pushed.",
  },
  {
    id: "setStatus",
    tab: "sa",
    method: "GET",
    path: "?action=setStatus",
    summary: "Change the activation status",
    auth: true,
    params: [
      SA_KEY,
      { name: "id", type: "string", desc: "activationId" },
      { name: "status", type: "int", desc: "1, 3, 6 or 8, see the table below" },
    ],
    request: `curl "${SA}?api_key=YOUR_API_KEY&action=setStatus&id=12345&status=8"`,
    response: `ACCESS_CANCEL\n\n# BAD_STATUS     not allowed in the current state\n# NO_ACTIVATION  unknown id\n# BAD_ACTION     unknown status value`,
    // Table des valeurs de status, rendue sous la table des parametres.
    statusTable: [
      { code: 1, answer: "ACCESS_READY", desc: "Number ready, waiting for the SMS (no change)" },
      { code: 3, answer: "ACCESS_RETRY_GET", desc: "Wait for another SMS on the same number" },
      { code: 6, answer: "ACCESS_ACTIVATION", desc: "Finish: only after a code was received" },
      { code: 8, answer: "ACCESS_CANCEL", desc: "Cancel and refund: only while no SMS was received" },
    ],
    errors: "",
  },
  {
    id: "getPrices",
    tab: "sa",
    method: "GET",
    path: "?action=getPrices",
    summary: "Prices and availability",
    auth: true,
    params: [
      SA_KEY,
      { name: "country", type: "string", desc: "Optional filter: code or slug" },
      { name: "service", type: "string", desc: "Optional filter: code or slug" },
    ],
    request: `curl "${SA}?api_key=YOUR_API_KEY&action=getPrices&country=187&service=wa"`,
    response: `{\n  "187": {\n    "wa": { "cost": 0.25, "count": 3 }\n  }\n}`,
    errors: "cost = lowest price in USD. count = number of providers offering it, not a stock count.",
  },
  {
    id: "getCountries",
    tab: "sa",
    method: "GET",
    path: "?action=getCountries",
    summary: "List countries",
    auth: true,
    params: [SA_KEY],
    request: `curl "${SA}?api_key=YOUR_API_KEY&action=getCountries"`,
    response: `{\n  "187": { "id": 187, "eng": "usa", "visible": 1 },\n  "16": { "id": 16, "eng": "unitedkingdom", "visible": 1 }\n}`,
    errors: "",
  },
  {
    id: "getServicesList",
    tab: "sa",
    method: "GET",
    path: "?action=getServicesList",
    summary: "List services",
    auth: true,
    params: [
      SA_KEY,
      { name: "country", type: "string", desc: "Optional: code or slug" },
    ],
    request: `curl "${SA}?api_key=YOUR_API_KEY&action=getServicesList&country=187"`,
    response: `{\n  "status": "success",\n  "services": [\n    { "code": "wa", "name": "whatsapp" },\n    { "code": "tg", "name": "telegram" }\n  ]\n}`,
    errors: "",
  },
];

const ENDPOINTS_BY_TAB = { rest: ENDPOINTS, sa: SA_ENDPOINTS };

// Ancre : #sa-<id> pour sms-activate, #api-<id> pour REST.
const anchorOf = (e) => (e.tab === "sa" ? `sa-${e.id}` : `api-${e.id}`);
// Prefixe des cles de traduction de la carte.
const prefixOf = (e) => (e.tab === "sa" ? "api.sa.ep" : "api.ep");

// Onglet deduit du hash de depart (#sms-activate, #sa-...) ; la page est prerendue.
const tabFromHash = () => {
  if (typeof window === "undefined") return "rest";
  const h = window.location.hash || "";
  return h.startsWith("#sms-activate") || h.startsWith("#sa-") ? "sa" : "rest";
};

// Endpoint selectionne au depart : celui de l'ancre si elle correspond, sinon
// le premier de l'onglet.
const initialActive = () => {
  const tab = tabFromHash();
  if (typeof window !== "undefined") {
    const h = window.location.hash || "";
    const prefix = tab === "sa" ? "#sa-" : "#api-";
    const found = h.startsWith(prefix) && ENDPOINTS_BY_TAB[tab].find((e) => e.id === h.slice(prefix.length));
    if (found) return found.id;
  }
  return ENDPOINTS_BY_TAB[tab][0].id;
};

const Copy = ({ text }) => {
  const { t } = useTranslation();
  const [ok, setOk] = useState(false);
  return (
    <button
      className="sg-copy"
      onClick={() => {
        copyText(text).then((done) => {
          if (!done) return;
          setOk(true);
          setTimeout(() => setOk(false), 1500);
        });
      }}
    >
      {ok ? `✓ ${t("signal.copied")}` : t("signal.copy")}
    </button>
  );
};

const ApiDocsPage = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(tabFromHash);
  const [active, setActive] = useState(initialActive);
  const tr = (key, fallback) => t(key, { defaultValue: fallback });
  const summaryOf = (e) => tr(`${prefixOf(e)}.${e.id}.summary`, e.summary);
  const list = ENDPOINTS_BY_TAB[tab];

  // Changement d'onglet : hash #sms-activate pour sms-activate (efface pour REST),
  // et selection du premier endpoint de l'onglet.
  const selectTab = (next) => {
    setTab(next);
    setActive(ENDPOINTS_BY_TAB[next][0].id);
    if (typeof window !== "undefined") {
      const base = `${window.location.pathname}${window.location.search}`;
      window.history.replaceState(null, "", next === "sa" ? `${base}#sms-activate` : base);
    }
  };

  // URL modifiee (lien #sa-getNumber...) : on suit l'onglet et l'ancre.
  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash || "";
      const next = tabFromHash();
      setTab(next);
      const prefix = next === "sa" ? "#sa-" : "#api-";
      const found = h.startsWith(prefix) && ENDPOINTS_BY_TAB[next].find((e) => e.id === h.slice(prefix.length));
      if (found) setActive(found.id);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  return (
    <>
      <PageSeo path="/api-docs" />
      <Helmet>
        <title>{t("api.seo-title")}</title>
        <meta
          name="description"
          content={t("api.seo-desc")}
        />
        <meta name="robots" content="index, follow" data-react-helmet="true" />
      </Helmet>

      <div className="sg-page">
        <div className="sg-cols">
          {/* Sidebar : endpoints de l'onglet actif seulement */}
          <div className="sg-panel api-side" style={{ flex: "0 0 200px", padding: 16 }}>
            <div className="sg-kicker" style={{ marginBottom: 10 }}>{t("api.reference")}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {list.map((e) => (
                <a
                  key={e.id}
                  href={`#${anchorOf(e)}`}
                  onClick={() => setActive(e.id)}
                  style={{
                    font: `${active === e.id ? "600" : "500"} 13px/1 var(--font-body)`,
                    color: active === e.id ? "var(--color-primary)" : "var(--color-text)",
                    background: active === e.id ? "var(--color-primary-soft)" : "transparent",
                    borderRadius: 5,
                    padding: "8px 10px",
                    textDecoration: "none",
                  }}
                >
                  {summaryOf(e)}
                </a>
              ))}
            </div>
          </div>

          {/* Contenu */}
          <div className="sg-col" style={{ flex: "1 1 640px" }}>
            <div className="sg-panel">
              <div className="sg-seg" style={{ maxWidth: 420, marginBottom: 14 }}>
                <button
                  type="button"
                  className={`sg-seg__opt${tab === "rest" ? " is-on" : ""}`}
                  onClick={() => selectTab("rest")}
                >
                  {t("api.tab-rest")}
                </button>
                <button
                  type="button"
                  className={`sg-seg__opt${tab === "sa" ? " is-on" : ""}`}
                  onClick={() => selectTab("sa")}
                >
                  {t("api.tab-sa")}
                </button>
              </div>

              {tab === "sa" ? (
                <>
                  <h1 className="sg-h1" style={{ fontSize: 24, marginBottom: 8 }}>{t("api.sa.title")}</h1>
                  <p className="sg-sub" style={{ fontSize: 14, maxWidth: "70ch", marginBottom: 14 }}>
                    <Trans i18nKey="api.sa.intro" components={{ code: CODE }} />
                  </p>
                  <div
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      border: "1px solid var(--color-line)", borderRadius: "var(--radius-field)",
                      background: "var(--color-canvas)", padding: "12px 14px", flexWrap: "wrap",
                    }}
                  >
                    <span className="sg-kicker sg-mono">{t("api.base-url")}</span>
                    <span className="sg-mono" style={{ font: "500 13px/1 var(--font-mono)", color: "var(--color-ink)", flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {SA}
                    </span>
                    <Copy text={SA} />
                  </div>
                  <ul style={{ margin: "12px 0 0", paddingLeft: 18, font: "400 13px/1.6 var(--font-body)", color: "var(--color-text)" }}>
                    <li><Trans i18nKey="api.sa.point1" components={{ code: CODE }} /></li>
                    <li><Trans i18nKey="api.sa.point2" components={{ code: CODE }} /></li>
                    <li><Trans i18nKey="api.sa.point3" components={{ code: CODE }} /></li>
                  </ul>
                </>
              ) : (
                <>
                  <h1 className="sg-h1" style={{ fontSize: 24, marginBottom: 8 }}>{t("api.title")}</h1>
                  <p className="sg-sub" style={{ fontSize: 14, maxWidth: "70ch", marginBottom: 14 }}>
                    <Trans i18nKey="api.intro" components={{ code: CODE }} />
                  </p>
                  <div
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      border: "1px solid var(--color-line)", borderRadius: "var(--radius-field)",
                      background: "var(--color-canvas)", padding: "12px 14px", flexWrap: "wrap",
                    }}
                  >
                    <span className="sg-kicker sg-mono">{t("api.base-url")}</span>
                    <span className="sg-mono" style={{ font: "500 13px/1 var(--font-mono)", color: "var(--color-ink)", flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {BASE}
                    </span>
                    <Copy text={BASE} />
                  </div>
                  <p className="sg-note">
                    <a href="#sms-activate" onClick={(ev) => { ev.preventDefault(); selectTab("sa"); }}>
                      {t("api.sa-hint")}
                    </a>
                  </p>
                </>
              )}
            </div>

            {list.map((e) => (
              <div key={e.id} id={anchorOf(e)} className="sg-panel">
                <div className="sg-head sg-head--center" style={{ marginBottom: 12 }}>
                  <span className={`sg-method sg-method--${e.method.toLowerCase()}`}>{e.method}</span>
                  <span className="sg-mono" style={{ font: "600 14px/1 var(--font-mono)", color: "var(--color-ink)", flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {e.path}
                  </span>
                  <span style={{ font: "400 12px var(--font-body)", color: "var(--color-muted)", flexShrink: 0 }}>
                    {summaryOf(e)}{e.auth ? ` · ${t("api.auth")}` : ""}
                  </span>
                </div>

                {e.params.length > 0 && (
                  <div className="sg-table api-params" style={{ "--cols": "120px 80px 1fr", marginBottom: 14 }}>
                    <div className="sg-tr sg-tr--head">
                      <span>{t("api.param")}</span><span>{t("api.type")}</span><span>{t("api.description")}</span>
                    </div>
                    {e.params.map((p) => (
                      <div key={p.name} className="sg-tr">
                        <span className="sg-td--mono" style={{ color: "var(--color-ink)", fontWeight: 500 }}>{p.name}</span>
                        <span className="sg-td--mono" style={{ color: "var(--color-muted)" }}>{p.type}</span>
                        <span>{tr(`${prefixOf(e)}.${e.id}.p.${p.name}`, p.desc)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {e.statusTable && (
                  <div className="sg-table api-params" style={{ "--cols": "90px 1fr 1fr", marginBottom: 14 }}>
                    <div className="sg-tr sg-tr--head">
                      <span>{t("api.sa.col-status")}</span><span>{t("api.sa.col-answer")}</span><span>{t("api.sa.col-meaning")}</span>
                    </div>
                    {e.statusTable.map((r) => (
                      <div key={r.code} className="sg-tr">
                        <span className="sg-td--num">{r.code}</span>
                        <span className="sg-td--mono" style={{ color: "var(--color-ink)" }}>{r.answer}</span>
                        <span>{tr(`api.sa.ep.setStatus.st.${r.code}`, r.desc)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="sg-head sg-head--center" style={{ margin: "0 0 8px" }}>
                  <span className="sg-kicker">{t("api.request")}</span>
                  <span className="sg-right"><Copy text={e.request} /></span>
                </div>
                <pre className="sg-code" style={{ margin: 0 }}>{e.request}</pre>

                <div className="sg-kicker" style={{ margin: "14px 0 8px" }}>{t("api.response")} · 200</div>
                <pre className="sg-code sg-code--light" style={{ margin: 0 }}>{e.response}</pre>

                {e.errors && (
                  <p className="sg-note" style={{ color: "var(--color-pending)" }}>
                    {t("api.errors")} — {tr(`${prefixOf(e)}.${e.id}.errors`, e.errors)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ApiDocsPage;
